import React, { useState } from 'react';
import {
  TextField,
  Box,
  Autocomplete,
  InputAdornment,
  Typography,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import useStore from 'src/store/cns';
import { useNavigate } from 'react-router-dom';

interface SearchBarProps {
  hint: string;
  onSearch?: (query: string) => void;
  onEnterPress?: (query: string) => void;
  validateInput?: (input: string) => boolean;
  openDomain?: (domain: string) => void;
}

const SearchBar: React.FC<SearchBarProps> = ({
  hint,
  onSearch,
  onEnterPress,
  validateInput,
  openDomain,
}) => {
  const [inputValue, setInputValue] = useState<string>('');
  const firstLvlDomains = useStore(state => state.firstLvlDomains);
  const navigate = useNavigate();

  const openDomainRegistration = (domainName: string) => {
    navigate('/cns', {
      state: {
        registerState: {
          domainName,
          status: 'registering',
        },
      },
    });
  };

  const handleInputChange = (newInputValue: string) => {
    if (!validateInput || validateInput(newInputValue)) {
      setInputValue(newInputValue);
      onSearch?.(newInputValue);
    }
  };

  const findMatchingDomain = (value: string) => {
    return firstLvlDomains.find(
      domain => domain.toLowerCase() === value.toLowerCase(),
    );
  };

  const handleSelection = (event: any, value: string | null) => {
    if (!value || value.length <= 3) {
      return;
    }

    const isKnownDomain = firstLvlDomains.some(domain =>
      value.toLowerCase().startsWith(domain.toLowerCase()),
    );

    if (!isKnownDomain && value.length > 3) {
      openDomainRegistration(value);
      return;
    }

    const matchedDomain = findMatchingDomain(value);
    const domain = matchedDomain ? `${matchedDomain}.cam` : value;

    if (domain && openDomain) {
      openDomain(domain);
      setInputValue('');
    } else {
      onEnterPress?.(inputValue);
    }
  };

  const getFilteredDomains = () => {
    return inputValue
      ? firstLvlDomains.filter(domain =>
          domain.toLowerCase().startsWith(inputValue.toLowerCase()),
        )
      : [];
  };

  const getOptions = () => {
    const filteredDomains = getFilteredDomains();
    const exactMatch = filteredDomains.some(
      domain => domain.toLowerCase() === inputValue.toLowerCase(),
    );
    return exactMatch || inputValue.length === 0
      ? filteredDomains
      : [inputValue, ...filteredDomains];
  };

  const renderRegisterButton = (option: string) => {
    return (
      <Button
        onClick={() => openDomainRegistration(option)}
        fullWidth
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          textTransform: 'none',
          padding: '8px 16px',
          color: 'inherit',
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
        disabled={inputValue.length <= 3}
      >
        <Typography variant="body1">{option}</Typography>
        <Typography
          variant="body2"
          sx={{ color: inputValue.length <= 3 ? 'info.error' : 'info.main' }}
        >
          {inputValue.length <= 3 ? 'Too Short' : 'Register'}
        </Typography>
      </Button>
    );
  };

  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
      data-cy="sd-searchbar"
    >
      <Autocomplete
        freeSolo
        fullWidth
        options={getOptions()}
        getOptionLabel={option => option}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) =>
          handleInputChange(newInputValue)
        }
        onChange={handleSelection}
        renderOption={(props, option) => (
          <li {...props} key={option}>
            {option === inputValue && !findMatchingDomain(option) ? (
              renderRegisterButton(option)
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                  padding: '8px 16px',
                }}
              >
                <Typography variant="body1">{option}</Typography>
              </Box>
            )}
          </li>
        )}
        renderInput={params => (
          <TextField
            {...params}
            fullWidth
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: inputValue ? (
                <InputAdornment position="end">
                  <Typography variant="body1" sx={{ color: 'info.main' }}>
                    .cam
                  </Typography>
                </InputAdornment>
              ) : null,
            }}
            placeholder={hint}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '48px',
                '&:hover fieldset': {
                  borderColor: 'grey.600',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'grey.600',
                },
              },
            }}
          />
        )}
        onKeyDown={e => {
          const filteredDomains = getFilteredDomains();
          if (e.key === 'Tab') {
            const matchingSuggestion = filteredDomains.find(suggestion =>
              suggestion.startsWith(inputValue),
            );
            if (matchingSuggestion) {
              e.preventDefault();
              setInputValue(matchingSuggestion);
            }
          } else if (
            inputValue.length > 3 &&
            e.key === 'Enter' &&
            filteredDomains.length === 1
          ) {
            e.preventDefault();
          }
        }}
      />
    </Box>
  );
};

export default SearchBar;
